import { useEffect, useState } from "react";
import { Check, X, Infinity as InfinityIcon, Sparkles, CreditCard, QrCode } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { money, fmtDate } from "../../lib/format";
import PageHeader from "../../components/PageHeader";

const FEATURE_LABELS = {
  POS: "POS",
  ORDERS: "Orders",
  REPORTS: "Reports",
  INVENTORY: "Inventory",
  KITCHEN: "Kitchen",
  MULTI_OUTLET: "Multi Outlet",
  DISCOUNTS: "Discounts",
  ADVANCED_REPORTS: "Advanced Reports",
  AUDIT_LOG: "Audit Log",
  OFFLINE_POS: "Offline POS",
  RECEIPT_PRINTING: "Receipt Printing",
  BARCODE: "Barcode",
};

const STATUS_STYLE = {
  ACTIVE: "bg-emerald-500/15 text-emerald-400",
  SUSPENDED: "bg-red-500/15 text-red-400",
  EXPIRED: "bg-red-500/15 text-red-400",
};

function jakartaDateString() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jakarta",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function getPlanPrice(plan, interval) {
  if (plan?.code === "FREE") return { amount: 0, isPromo: false };

  const options = plan?.pricing_options || {};
  const selected = options[interval] || {};
  const regular = selected.price ?? (interval === "MONTHLY" ? plan?.price : null);
  const promo = selected.launch_price;
  const promotion = plan?.promotion || {};
  const today = jakartaDateString();

  const promoActive =
    promotion.enabled === true &&
    Boolean(promotion.start_date) &&
    Boolean(promotion.end_date) &&
    today >= promotion.start_date &&
    today <= promotion.end_date &&
    promo != null &&
    Number.isSafeInteger(Number(promo)) &&
    Number(promo) > 0;

  return {
    amount: Number(promoActive ? promo : regular || 0),
    isPromo: promoActive,
    regular: Number(regular || 0),
  };
}

function UsageBar({ label, used, limit, testid }) {
  const pct =
    limit == null
      ? 0
      : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));

  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono font-bold flex items-center gap-1">
          {used} /{" "}
          {limit == null ? (
            <InfinityIcon size={14} className="text-primary" />
          ) : (
            limit
          )}
        </span>
      </div>

      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            pct >= 90
              ? "bg-red-400"
              : pct >= 70
                ? "bg-amber-400"
                : "bg-primary"
          }`}
          style={{
            width: limit == null ? "8%" : `${pct}%`,
          }}
        />
      </div>
    </div>
  );
}

export default function SubscriptionPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [allPlans, setAllPlans] = useState([]);
  const [billingInterval, setBillingInterval] = useState("MONTHLY");

  const [checkoutPlan, setCheckoutPlan] = useState("");
  const [checkoutError, setCheckoutError] = useState("");

  const [qrisInvoice, setQrisInvoice] = useState(null);
  const [qrisImageUrl, setQrisImageUrl] = useState("");
  const [qrisLoading, setQrisLoading] = useState(false);
  const [qrisSubmitting, setQrisSubmitting] = useState(false);
  const [qrisMessage, setQrisMessage] = useState("");
  const [qrisError, setQrisError] = useState("");
  const [qrisProofMode, setQrisProofMode] = useState(false);
  const [qrisProofFile, setQrisProofFile] = useState(null);

  useEffect(() => {
    api
      .get("/subscription")
      .then((r) => setData(r.data))
      .catch((e) => setError(apiError(e)));

    api
      .get("/subscription/plans")
      .then((r) => setAllPlans(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let objectUrl = "";

    const loadImage = async () => {
      if (!qrisInvoice?.id) {
        setQrisImageUrl("");
        return;
      }

      try {
        const response = await api.get(
          `/billing/qris/${qrisInvoice.id}/image`,
          {
            responseType: "blob",
          }
        );

        objectUrl = URL.createObjectURL(response.data);
        setQrisImageUrl(objectUrl);
      } catch (e) {
        setQrisImageUrl("");
        setQrisError(
          apiError(e) || "QRIS image gagal dimuat."
        );
      }
    };

    loadImage();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [qrisInvoice?.id]);

  const pendingActivation = Boolean(
    data?.subscription?.pending_activation
  );

  const requestedPlan = (
    data?.subscription?.requested_plan_code || ""
  ).toUpperCase();

  const dismissBanner = () => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            subscription: {
              ...prev.subscription,
              pending_activation: false,
            },
          }
        : prev
    );

    api.post("/subscription/dismiss-pending").catch(() => {});
  };

  const startQrisCheckout = async (planCode, interval = billingInterval) => {
    setCheckoutPlan(planCode);
    setCheckoutError("");
    setQrisError("");
    setQrisMessage("");
    setQrisInvoice(null);
    setQrisImageUrl("");
    setQrisLoading(true);

    try {
      const response = await api.post("/billing/qris/checkout", {
        plan_code: planCode,
        billing_interval: interval,
      });

      const invoice = response?.data?.invoice;

      if (!invoice?.id) {
        throw new Error("Invoice QRIS tidak berhasil dibuat.");
      }

      setQrisInvoice(invoice);

      setQrisMessage(
        response?.data?.reused
          ? "Pembayaran QRIS yang sebelumnya dibuat ditemukan kembali."
          : "Invoice QRIS berhasil dibuat. Silakan lakukan pembayaran sesuai nominal."
      );
    } catch (e) {
      setCheckoutError(apiError(e));
      setCheckoutPlan("");
    } finally {
      setQrisLoading(false);
    }
  };

  const openPendingQris = async () => {
    if (!requestedPlan || requestedPlan === "FREE") return;

    const pendingInterval =
      data?.subscription?.requested_billing_interval || "MONTHLY";
    await startQrisCheckout(requestedPlan, pendingInterval);
  };

  const submitPendingQris = async () => {
    if (!requestedPlan || requestedPlan === "FREE") return;

    setCheckoutPlan(requestedPlan);
    setCheckoutError("");
    setQrisError("");
    setQrisMessage("");
    setQrisProofMode(false);
    setQrisProofFile(null);
    setQrisLoading(true);

    try {
      const response = await api.post("/billing/qris/checkout", {
        plan_code: requestedPlan,
        billing_interval:
          data?.subscription?.requested_billing_interval || "MONTHLY",
      });

      const invoice = response?.data?.invoice;

      if (!invoice?.id) {
        throw new Error("Invoice QRIS tidak berhasil ditemukan.");
      }

      setQrisInvoice(invoice);

      if (invoice.status === "WAITING_VERIFICATION") {
        setQrisMessage(
          "Pembayaran ini sudah dikonfirmasi dan sedang menunggu verifikasi GLOOS."
        );
      } else {
        setQrisMessage(
          "Silakan lakukan pembayaran sesuai nominal, lalu upload bukti pembayaran."
        );
      }
    } catch (e) {
      setQrisError(apiError(e));
    } finally {
      setQrisLoading(false);
      setCheckoutPlan("");
    }
  };

  const submitQrisPayment = () => {
    if (!qrisInvoice?.id) return;

    if (qrisInvoice.status === "WAITING_VERIFICATION") {
      setQrisMessage(
        "Pembayaran ini sudah dikonfirmasi dan sedang menunggu verifikasi GLOOS."
      );
      return;
    }

    setQrisError("");
    setQrisMessage("");
    setQrisProofFile(null);
    setQrisProofMode(true);
  };

  const submitQrisProof = async () => {
    if (!qrisInvoice?.id) return;

    if (!qrisProofFile) {
      setQrisError("Silakan pilih bukti pembayaran terlebih dahulu.");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png"];
    const maxSize = 2 * 1024 * 1024;

    if (!allowedTypes.includes(qrisProofFile.type)) {
      setQrisError("Format bukti pembayaran harus JPG, JPEG, atau PNG.");
      return;
    }

    if (qrisProofFile.size > maxSize) {
      setQrisError("Ukuran bukti pembayaran maksimal 2 MB.");
      return;
    }

    setQrisSubmitting(true);
    setQrisError("");
    setQrisMessage("");

    try {
      const formData = new FormData();
      formData.append("file", qrisProofFile);

      const response = await api.post(
        `/billing/qris/${qrisInvoice.id}/proof`,
        formData
      );

      const updatedInvoice = response?.data?.invoice;

      if (updatedInvoice) {
        setQrisInvoice(updatedInvoice);
      }

      setQrisProofMode(false);
      setQrisProofFile(null);
      setQrisMessage(
        "Bukti pembayaran berhasil dikirim dan sedang menunggu verifikasi GLOOS."
      );

    } catch (e) {
      if (e?.response?.status === 413) {
        setQrisError("Ukuran upload melebihi batas yang diizinkan. Maksimal gambar 2 MB. Silakan pilih gambar yang lebih kecil.");
      } else if (!e?.response) {
        setQrisError("Bukti pembayaran belum berhasil diunggah karena koneksi bermasalah. Periksa internet lalu coba kembali.");
      } else {
        setQrisError(apiError(e));
      }
    } finally {
      setQrisSubmitting(false);
    }
  };

  const cancelQrisProof = () => {
    setQrisProofMode(false);
    setQrisProofFile(null);
    setQrisError("");
  };

  const closeQrisPayment = () => {
    setQrisInvoice(null);
    setQrisImageUrl("");
    setQrisError("");
    setQrisMessage("");
    setQrisProofMode(false);
    setQrisProofFile(null);
    setCheckoutPlan("");
  };

  if (error) {
    return (
      <p
        className="text-sm text-red-400"
        data-testid="subscription-error"
      >
        {error}
      </p>
    );
  }

  if (!data) {
    return (
      <p className="text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }

  const { plan, subscription: sub, features, usage } = data;
  const featureEntries = Object.entries(FEATURE_LABELS);

  const qrisWaiting =
    qrisInvoice?.status === "WAITING_VERIFICATION";

  const qrisPaid =
    qrisInvoice?.status === "PAID";

  return (
    <div data-testid="subscription-page">
      <PageHeader
        title="Subscription"
        subtitle="Your plan, entitlements and usage — managed by the GLOOS platform"
        testid="subscription-header"
      />

      {pendingActivation &&
        requestedPlan &&
        requestedPlan !== "FREE" && (
          <div
            data-testid="pending-activation-banner"
            className="mb-4 rounded-2xl border border-primary/40 bg-primary/10 px-5 py-4"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                <Sparkles size={18} className="text-primary" />
              </div>

              <div className="flex-1 space-y-1">
                <p className="font-heading font-bold text-sm">
                  Permintaan paket{" "}
                  <span className="text-primary">
                    {requestedPlan}
                  </span>{" "}
                  sedang diproses
                </p>

                <p className="text-xs text-muted-foreground leading-relaxed">
                  Pembayaran subscription sedang menunggu proses
                  verifikasi. Setelah pembayaran dikonfirmasi oleh
                  GLOOS, paket akan diaktifkan.
                </p>
              </div>

              <button
                onClick={dismissBanner}
                data-testid="pending-activation-dismiss"
                className="w-8 h-8 rounded-lg bg-secondary hover:bg-accent flex items-center justify-center shrink-0"
                aria-label="Tutup"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-2 pl-12">
              <button
                type="button"
                onClick={openPendingQris}
                disabled={qrisLoading || Boolean(checkoutPlan)}
                data-testid="pending-activation-show-qris"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-xs font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <QrCode size={14} />
                {qrisLoading
                  ? "Menyiapkan QRIS…"
                  : "Tampilkan QRIS Pembayaran"}
              </button>

              <button
                type="button"
                onClick={submitPendingQris}
                disabled={
                  qrisLoading ||
                  qrisSubmitting ||
                  Boolean(checkoutPlan)
                }
                data-testid="pending-activation-paid"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-primary/40 bg-background/40 px-4 py-2.5 text-xs font-semibold hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {qrisSubmitting
                  ? "Mengirim konfirmasi…"
                  : "Saya Sudah Bayar"}
              </button>
            </div>
          </div>
        )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3
              className="font-heading text-2xl font-extrabold text-primary"
              data-testid="subscription-plan-name"
            >
              {plan.name}
            </h3>

            <span
              className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${
                STATUS_STYLE[sub.status] ||
                "bg-secondary text-muted-foreground"
              }`}
              data-testid="subscription-status"
            >
              {sub.status}
            </span>
          </div>

          <p className="text-sm text-muted-foreground">
            {plan.description}
          </p>

          {(() => {
            const activeInterval =
              data?.pricing?.billing_interval ||
              sub?.billing_interval ||
              plan.billing_interval ||
              "MONTHLY";
            const activePrice = getPlanPrice(plan, activeInterval);

            return (
              <div data-testid="active-plan-price">
                {activePrice.isPromo && (
                  <p className="text-sm text-muted-foreground line-through">
                    {money(activePrice.regular)}
                  </p>
                )}
                <p className="font-mono text-xl font-bold">
                  {money(activePrice.amount)}
                  <span className="text-xs text-muted-foreground font-sans">
                    {" "}
                    /{" "}
                    {activeInterval === "YEARLY" ? "year" : "month"}
                  </span>
                </p>
                {activePrice.isPromo && (
                  <span className="inline-block mt-1 px-2 py-1 rounded-md bg-amber-500/10 text-amber-400 text-[10px] font-bold">
                    PROMO
                  </span>
                )}
              </div>
            );
          })()}

          <div className="text-sm space-y-1.5 border-t border-border pt-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Period start
              </span>
              <span data-testid="subscription-period-start">
                {fmtDate(sub.current_period_start)}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Next renewal
              </span>
              <span data-testid="subscription-period-end">
                {fmtDate(sub.current_period_end)}
              </span>
            </div>
          </div>

          {!data.accessible && (
            <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              Subscription is not currently active. Contact GLOOS
              support. Your data is preserved.
            </p>
          )}
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
          <h3 className="font-heading font-semibold">
            Usage & Limits
          </h3>

          <UsageBar
            label="Outlets"
            used={usage.outlets.used}
            limit={usage.outlets.limit}
            testid="usage-outlets"
          />

          <UsageBar
            label="Users"
            used={usage.users.used}
            limit={usage.users.limit}
            testid="usage-users"
          />

          <p className="text-[11px] text-muted-foreground">
            Limits are enforced server-side and concurrency-safe.
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6">
          <h3 className="font-heading font-semibold mb-4">
            Feature Access
          </h3>

          <div className="grid grid-cols-2 gap-2">
            {featureEntries.map(([code, label]) => {
              const on = features[code]?.enabled;

              return (
                <div
                  key={code}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${
                    on
                      ? "bg-emerald-500/10 text-emerald-400"
                      : "bg-secondary/60 text-muted-foreground"
                  }`}
                  data-testid={`feature-${code.toLowerCase()}`}
                >
                  {on ? <Check size={13} /> : <X size={13} />}
                  {label}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {!pendingActivation && (() => {
        const upgradePlans =
          plan.code === "FREE"
            ? allPlans.filter((p) => ["BASIC", "PRO"].includes(p.code))
            : plan.code === "BASIC"
              ? allPlans.filter((p) => p.code === "PRO")
              : [];

        if (upgradePlans.length === 0) return null;

        return (
          <div className="mt-4 bg-card border border-border rounded-2xl p-6">
            <h3 className="font-heading font-semibold mb-1">
              Upgrade Paket
            </h3>

            <p className="text-xs text-muted-foreground mb-4">
              Pilih paket yang sesuai kebutuhan bisnis Anda, lalu
              lakukan pembayaran menggunakan QRIS GLOOS.
            </p>

            {checkoutError && (
              <div
                data-testid="billing-checkout-error"
                className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-400"
              >
                {checkoutError}
              </div>
            )}

            <div className="flex gap-2 mb-4">
              {[
                { value: "MONTHLY", label: "Bulanan" },
                { value: "YEARLY", label: "Tahunan" },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setBillingInterval(option.value)}
                  aria-pressed={billingInterval === option.value}
                  className={`flex-1 rounded-lg border px-4 py-2 text-sm font-semibold ${
                    billingInterval === option.value
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground"
                  }`}
                  data-testid={`billing-interval-${option.value.toLowerCase()}`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div
              className={`grid grid-cols-1 ${
                upgradePlans.length === 1
                  ? "sm:grid-cols-1"
                  : "sm:grid-cols-2"
              } gap-3`}
            >
              {upgradePlans.map((p) => {
                const loading =
                  checkoutPlan === p.code && qrisLoading;
                const priceInfo = getPlanPrice(p, billingInterval);
                const displayPrice = priceInfo.amount;

                return (
                  <div
                    key={p.code}
                    className="border border-border rounded-xl p-4 space-y-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-bold text-primary">
                        {p.name}
                      </p>

                      {priceInfo.isPromo && (
                        <span className="px-2 py-1 rounded-md bg-amber-500/10 text-amber-400 text-[10px] font-bold">
                          PROMO
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed min-h-[40px]">
                      {p.description ||
                        "Paket subscription GLOOS untuk kebutuhan bisnis Anda."}
                    </p>

                    {Array.isArray(p.benefits) && p.benefits.length > 0 && (
                      <div className="space-y-2 pt-1">
                        {p.benefits.map((benefit, index) => (
                          <div
                            key={`${p.code}-benefit-${index}`}
                            className="flex items-start gap-2 text-xs text-muted-foreground"
                          >
                            <Check
                              size={13}
                              className="mt-0.5 shrink-0 text-emerald-400"
                            />
                            <span>{benefit}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div>
                      {priceInfo.isPromo && (
                        <p className="text-xs text-muted-foreground line-through">
                          {money(priceInfo.regular)}
                        </p>
                      )}
                      <p className="font-mono text-lg font-bold">
                        {money(displayPrice)}
                        <span className="text-xs text-muted-foreground font-sans">
                          {billingInterval === "YEARLY" ? " /tahun" : " /bulan"}
                        </span>
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => startQrisCheckout(p.code, billingInterval)}
                      disabled={Boolean(checkoutPlan)}
                      data-testid={`billing-checkout-${p.code.toLowerCase()}`}
                      className="w-full flex items-center justify-center gap-2 text-xs font-semibold bg-primary text-primary-foreground rounded-lg py-2 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <QrCode size={14} />
                      {loading
                        ? "Menyiapkan QRIS…"
                        : "Bayar dengan QRIS"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {qrisInvoice && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          data-testid="qris-payment-modal"
        >
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-2xl">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-lg">
                  Pembayaran QRIS
                </h3>

                <p className="text-xs text-muted-foreground mt-1">
                  GLOOS POS Subscription
                </p>
              </div>

              <button
                type="button"
                onClick={closeQrisPayment}
                className="w-9 h-9 rounded-lg bg-secondary hover:bg-accent flex items-center justify-center"
                aria-label="Tutup"
              >
                <X size={17} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="rounded-xl bg-secondary/50 p-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    Paket
                  </span>
                  <span className="font-semibold">
                    {qrisInvoice.plan_name ||
                      qrisInvoice.plan_code}
                  </span>
                </div>

                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-muted-foreground">
                    Total pembayaran
                  </span>
                  <span className="font-mono text-xl font-bold text-primary">
                    {money(qrisInvoice.amount)}
                  </span>
                </div>
              </div>

              {qrisImageUrl ? (
                <div className="bg-white rounded-2xl p-4 flex justify-center">
                  <img
                    src={qrisImageUrl}
                    alt="QRIS pembayaran GLOOS"
                    className="w-full max-w-[300px] aspect-square object-contain"
                  />
                </div>
              ) : (
                <div className="h-64 rounded-2xl bg-secondary flex flex-col items-center justify-center text-center p-6">
                  <QrCode size={42} className="text-muted-foreground mb-3" />
                  <p className="text-sm font-semibold">
                    Memuat QRIS…
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Tunggu sebentar.
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-border p-4 text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Merchant
                  </span>
                  <span className="font-semibold">
                    {qrisInvoice.qris?.merchant_name || "GLOO"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Kota
                  </span>
                  <span>
                    {qrisInvoice.qris?.merchant_city || "—"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Mata uang
                  </span>
                  <span>
                    {qrisInvoice.currency || "IDR"}
                  </span>
                </div>
              </div>

              {qrisMessage && (
                <div className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-xs text-primary">
                  {qrisMessage}
                </div>
              )}

              {qrisError && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-400">
                  {qrisError}
                </div>
              )}

              {qrisWaiting ? (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-4">
                  <p className="font-semibold text-sm text-amber-400">
                    Menunggu verifikasi pembayaran
                  </p>

                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    Bukti pembayaran sudah diterima.
                    GLOOS akan memeriksa mutasi pembayaran sebelum
                    mengaktifkan paket.
                  </p>
                </div>
              ) : qrisPaid ? (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-4">
                  <p className="font-semibold text-sm text-emerald-400">
                    Pembayaran diterima
                  </p>

                  <p className="text-xs text-muted-foreground mt-1">
                    Paket subscription telah diaktifkan.
                  </p>
                </div>
              ) : qrisProofMode ? (
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-4">
                  <div>
                    <p className="font-semibold text-sm">
                      Bukti Pembayaran
                    </p>

                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Upload screenshot atau foto bukti pembayaran QRIS.
                      Format JPG, JPEG, atau PNG dengan ukuran maksimal 2 MB.
                    </p>
                  </div>

                  <div className="rounded-xl border border-dashed border-border bg-secondary/40 p-4">
                    <input
                      id="qris-payment-proof"
                      type="file"
                      accept="image/jpeg,image/png"
                      className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-primary file:px-3 file:py-2 file:text-xs file:font-semibold file:text-primary-foreground hover:file:opacity-90"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setQrisError("");

                        if (!file) {
                          setQrisProofFile(null);
                          return;
                        }

                        const allowedTypes = [
                          "image/jpeg",
                          "image/png",
                        ];
                        const maxSize = 2 * 1024 * 1024;

                        if (!allowedTypes.includes(file.type)) {
                          setQrisProofFile(null);
                          setQrisError(
                            "Format bukti pembayaran harus JPG, JPEG, atau PNG."
                          );
                          e.target.value = "";
                          return;
                        }

                        if (file.size > maxSize) {
                          setQrisProofFile(null);
                          setQrisError(
                            "Ukuran bukti pembayaran maksimal 2 MB."
                          );
                          e.target.value = "";
                          return;
                        }

                        setQrisProofFile(file);
                      }}
                    />

                    {qrisProofFile && (
                      <p className="text-xs text-muted-foreground mt-3">
                        File dipilih:{" "}
                        <span className="font-medium text-foreground">
                          {qrisProofFile.name}
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={cancelQrisProof}
                      disabled={qrisSubmitting}
                      className="flex-1 rounded-lg border border-border bg-secondary px-4 py-2.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
                    >
                      Batalkan
                    </button>

                    <button
                      type="button"
                      onClick={submitQrisProof}
                      disabled={qrisSubmitting || !qrisProofFile}
                      className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {qrisSubmitting
                        ? "Mengirim…"
                        : "Konfirmasi Pembayaran"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={submitQrisPayment}
                  disabled={
                    qrisSubmitting || !qrisImageUrl
                  }
                  className="w-full flex items-center justify-center gap-2 text-sm font-bold bg-primary text-primary-foreground rounded-xl py-3 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="qris-payment-submit"
                >
                  <CreditCard size={16} />
                  {qrisSubmitting
                    ? "Menyiapkan bukti pembayaran…"
                    : "Saya Sudah Bayar"}
                </button>
              )}

              <p className="text-[11px] text-center text-muted-foreground leading-relaxed">
                Pastikan nominal pembayaran sesuai invoice sebelum
                melakukan transfer. Setelah menekan “Saya Sudah
                Bayar”, pembayaran akan menunggu verifikasi GLOOS.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
